/* global Excel, Office */
// Writes a workbook plan (workbookPlan.js) into Excel and reads v2 sheets back. Sheets are
// recognised by the key in row 1 of their hidden KEY column, never by tab name, so renamed and
// renumbered sheets keep their content.

import { SHEET_KEY_PREFIX, colLetter, parseDisciplineSheet } from "./sheetModel.js";

const KEY_SCAN = "A1:AD1";
const MONEY = "#,##0.00";
const QTY = "#,##0.00##";
const PCT = "0.0%";

const STYLE = {
  band: { fill: "#1E6E6E", color: "#FFFFFF", bold: true, size: 12 },
  title: { bold: true, size: 12 },
  info: { color: "#3F4453" },
  head: { fill: "#F2F2F5", bold: true, wrap: true, center: true, border: true },
  colno: { color: "#8A8FA0", center: true, size: 9 },
  block: { fill: "#2F3542", color: "#FFFFFF", bold: true },
  hint: { italic: true, color: "#8A8FA0" },
  loc: { fill: "#EEF0F6", bold: true },
  work: { bold: true },
  note: { italic: true, color: "#5E6373" },
  ltot: { italic: true, bold: true },
  btot: { fill: "#F6F6FA", bold: true, topBorder: true },
  sum: { fill: "#E6E3FC", bold: true, topBorder: true },
  mk: { italic: true },
  cat: { fill: "#DDE7F0", bold: true },
  line1: { bold: true },
};

const WORKS_WIDTHS = { n: 48, code: 90, name: 360, en: 260, unit: 64, norm: 70, qty: 70, matP: 76, matT: 90, labP: 76, labT: 90, eqP: 76, eqT: 90, total: 100, note: 110, loc: 140 };
const SIMPLE_WIDTHS = { n: 48, name: 380, en: 260, unit: 64, qty: 70, price: 90, total: 110, note: 160, loc: 140 };
const TOTAL_WIDTHS = [48, 420, 100, 100, 100, 120, 56];

/** Identity of a v2 sheet from the key in row 1: "e1|n_h22", "e1|TOTAL", "plot|PLOT". */
const identityOf = (row1) => {
  const key = (row1 || []).find((v) => String(v).startsWith(SHEET_KEY_PREFIX));
  if (!key) return null;
  const [, , objectId, nodeId, kind, grouped] = String(key).split("|");
  return { id: `${objectId}|${nodeId}`, objectId, nodeId, kind, grouped: grouped === "t" };
};
const identityOfPlan = (sheet) => identityOf(sheet.model.values[0]).id;

async function scanWorkbook(context) {
  const sheets = context.workbook.worksheets;
  sheets.load("items/name");
  await context.sync();
  const heads = sheets.items.map((ws) => {
    const r = ws.getRange(KEY_SCAN);
    r.load("values");
    return { ws, r };
  });
  await context.sync();
  return heads.map(({ ws, r }) => ({ ws, name: ws.name, identity: identityOf(r.values[0]) }));
}

/**
 * Read every v2 discipline sheet that holds its own works (not linked copies).
 * Returns { "<objectId>|<nodeId>": rows }.
 */
export async function readV2Contents() {
  return Excel.run(async (context) => {
    const found = (await scanWorkbook(context)).filter(
      (s) => s.identity && s.identity.nodeId !== "TOTAL" && s.identity.nodeId !== "PLOT" && !s.identity.objectId.includes("=")
    );
    const ranges = found.map((s) => {
      const r = s.ws.getRange("A1").getBoundingRect(s.ws.getUsedRange());
      r.load("values");
      return { s, r };
    });
    await context.sync();
    const out = {};
    ranges.forEach(({ s, r }) => {
      const parsed = parseDisciplineSheet(r.values);
      if (parsed) out[`${parsed.objectId}|${parsed.nodeId}`] = parsed.rows;
    });
    return out;
  });
}

/**
 * What writing the plan would do: v2 sheets that would be removed (with their row counts) and
 * tabs taken by sheets that are not Costy v2 sheets (writing is refused while they exist).
 */
export async function inspectWorkbook(plan) {
  return Excel.run(async (context) => {
    const existing = await scanWorkbook(context);
    const planned = new Set(plan.sheets.map(identityOfPlan));
    const plannedTabs = new Set(plan.sheets.map((s) => s.tab.toLowerCase()));
    const toRemove = existing.filter((s) => s.identity && !planned.has(s.identity.id)).map((s) => ({ name: s.name, identity: s.identity.id }));
    const conflicts = existing.filter((s) => !s.identity && plannedTabs.has(s.name.toLowerCase())).map((s) => s.name);
    return { toRemove, conflicts, existingV2: existing.filter((s) => s.identity).length };
  });
}

function styleRow(ws, row, width, style) {
  const s = STYLE[style];
  if (!s) return;
  const range = ws.getRange(`A${row}:${colLetter(width - 2)}${row}`);
  if (s.fill) range.format.fill.color = s.fill;
  if (s.color) range.format.font.color = s.color;
  if (s.bold) range.format.font.bold = true;
  if (s.italic) range.format.font.italic = true;
  if (s.size) range.format.font.size = s.size;
  if (s.wrap) range.format.wrapText = true;
  if (s.center) {
    range.format.horizontalAlignment = "Center";
    range.format.verticalAlignment = "Center";
  }
  if (s.border) ["EdgeTop", "EdgeBottom", "EdgeLeft", "EdgeRight", "InsideVertical"].forEach((e) => (range.format.borders.getItem(e).style = "Continuous"));
  if (s.topBorder) range.format.borders.getItem("EdgeTop").style = "Continuous";
}

function writeModel(ws, sheet, groupingSupported) {
  const { model } = sheet;
  const rows = model.values.length;
  const width = model.width;
  const last = colLetter(width - 1);
  ws.getRange().unmerge();
  ws.getRange().clear();
  ws.getRange().rowHidden = false;
  ws.getRange(`A1:${last}${rows}`).formulas = model.values;
  model.merges.forEach((m) => ws.getRange(m).merge(false));
  model.styles.forEach(({ row, style }) => styleRow(ws, row, width, style));
  ws.getRange(`${model.keyCol}:${model.keyCol}`).columnHidden = true;

  if (sheet.role === "discipline") {
    const widths = model.kind === "simple" ? SIMPLE_WIDTHS : WORKS_WIDTHS;
    Object.entries(model.col).forEach(([k, c]) => {
      if (widths[k]) ws.getRange(`${c}:${c}`).format.columnWidth = widths[k];
    });
    const money = model.kind === "simple" ? ["price", "total"] : ["matP", "matT", "labP", "labT", "eqP", "eqT", "total"];
    money.forEach((k) => (ws.getRange(`${model.col[k]}8:${model.col[k]}${rows}`).numberFormat = [[MONEY]]));
    ["qty", "norm"].filter((k) => model.col[k]).forEach((k) => (ws.getRange(`${model.col[k]}8:${model.col[k]}${rows}`).numberFormat = [[QTY]]));
    ws.getRange(`${model.col.name}8:${model.col.name}${rows}`).format.wrapText = true;
    ws.freezePanes.freezeRows(6);
    model.groups.forEach(([from, to]) => {
      const range = ws.getRange(`${from}:${to}`);
      if (groupingSupported) range.group(Excel.GroupOption.byRows);
      else range.rowHidden = true;
    });
    if (groupingSupported && model.groups.length) ws.showOutlineLevels(1, 0);
  } else {
    TOTAL_WIDTHS.forEach((w, i) => (ws.getRange(`${colLetter(i)}:${colLetter(i)}`).format.columnWidth = w));
    ws.getRange(`C8:F${rows}`).numberFormat = [[MONEY]];
    ws.getRange(`G8:G${rows}`).numberFormat = [[PCT]];
    ws.freezePanes.freezeRows(7);
  }
}

/**
 * Write the plan. v2 sheets are matched by identity and renamed as needed; sheets in
 * `removeIds` are deleted (the caller confirms that first). Discipline sheets are written
 * before TOTALs and the plot sheet so their formulas point at existing sheets.
 */
export async function writeWorkbook(plan, { removeIds = [], onProgress } = {}) {
  const groupingSupported = typeof Office !== "undefined" && !!Office.context?.requirements?.isSetSupported?.("ExcelApi", "1.10");
  return Excel.run(async (context) => {
    const existing = await scanWorkbook(context);
    const byIdentity = new Map(existing.filter((s) => s.identity).map((s) => [s.identity.id, s.ws]));

    // ungroup sheets written in tender mode one by one: ungroup fails on a sheet without groups
    for (const s of existing.filter((x) => x.identity?.grouped && !removeIds.includes(x.identity.id))) {
      try {
        s.ws.getRange("A:A").getEntireRow().ungroup(Excel.GroupOption.byRows);
        await context.sync();
      } catch (e) {
        // already ungrouped by the user
      }
    }
    existing.filter((s) => s.identity && removeIds.includes(s.identity.id)).forEach((s) => s.ws.delete());
    // free every tab first, so two sheets can swap names
    existing.filter((s) => s.identity && !removeIds.includes(s.identity.id)).forEach((s, i) => (s.ws.name = `~costy${i}`));
    await context.sync();

    const targets = plan.sheets.map((sheet) => {
      const ws = byIdentity.get(identityOfPlan(sheet)) || context.workbook.worksheets.add();
      ws.name = sheet.tab;
      return { sheet, ws };
    });
    await context.sync();

    const ordered = [...targets.filter((t) => t.sheet.role === "discipline"), ...targets.filter((t) => t.sheet.role !== "discipline").reverse()];
    for (let i = 0; i < ordered.length; i++) {
      writeModel(ordered[i].ws, ordered[i].sheet, groupingSupported);
      if (i % 4 === 3 || i === ordered.length - 1) {
        await context.sync();
        onProgress?.(i + 1, ordered.length);
      }
    }
    targets.forEach((t, i) => (t.ws.position = i));
    targets[0]?.ws.activate();
    await context.sync();
    return { written: targets.length, removed: removeIds.length };
  });
}
